// src/utils/formatters.ts
import { format, parseISO } from 'date-fns';

/**
 * Formats a number as currency (USD)
 * @param amount - The amount to format
 * @param minimumFractionDigits - Minimum number of fraction digits to show
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, minimumFractionDigits: number = 0): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Formats a phone number
 * @param phoneNumber - The phone number to format
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 11)}`;
  }
  return phoneNumber;
};

/**
 * Truncates text to a specified length
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Formats a date to a display-friendly string
 * @param dateString - ISO date string
 * @param format - Format to use (default: 'MMM d, yyyy')
 * @returns Formatted date string
 */
export const formatDate = (dateString: string, format: string = 'MMM d, yyyy'): string => {
  try {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Format appointment date and time
 * @param dateTimeString ISO date string
 * @returns Object with formatted date and time properties
 */
export const formatAppointmentDateTime = (dateTimeString: string) => {
  try {
    return {
      date: format(parseISO(dateTimeString), 'EEE, MMM d, yyyy'),
      time: format(parseISO(dateTimeString), 'h:mm a')
    };
  } catch (error) {
    console.error('Error formatting appointment date/time:', error);
    return {
      date: 'Invalid date',
      time: 'Invalid time'
    };
  }
};